import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { NationBuilderAccessTokenStatus } from "../models/schema/NationBuilderAccessTokenStatus";
import ApiClient, { ApiError } from "../api/ApiClient";
import { useApiClient } from "../hooks/useApiClient";
import { useAuth } from "./AuthProvider";
import { useNavigate } from "react-router-dom";
import { NationBuilderTokenAPIError } from "../models/errors/NationBuilderAPIError";

interface NationBuilderTokenContextType {
  tokenState: NationBuilderAccessTokenStatus;
  isLoading: boolean;
  error: string | null;
  handleNationBuilderSignIn: () => Promise<void>;
  refreshNationBuilderTokenValidityStatus: () => Promise<void>;
  handleError: (error: ApiError) => Promise<void>;
}

const NationBuilderTokenContext = createContext<
  NationBuilderTokenContextType | undefined
>(undefined);

interface NationBuilderTokenProviderProps {
  children: ReactNode;
}

export const NationBuilderTokenProvider: React.FC<
  NationBuilderTokenProviderProps
> = ({ children }) => {
  const [tokenState, setTokenState] = useState<NationBuilderAccessTokenStatus>(
    NationBuilderAccessTokenStatus.Loading,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const {
    isAuthenticated,
    accessToken,
    handleError: handleAuthError,
  } = useAuth();

  // Create ApiClient instance directly to avoid circular dependency
  const apiClient = new ApiClient(undefined, {
    getAccessToken: () => accessToken,
    handleAuthError: async (error: ApiError) => {
      return await handleAuthError(error);
    },
    handleNationBuilderTokenError: async (error: ApiError) => {
      return await handleError(error);
    },
  });

  const handleNationBuilderSignIn = async () => {
    /**
     * Initiates the OAuth sign-in process for NationBuilder integration.
     *
     * This function:
     * 1. Makes a request to the backend to get the OAuth authorization URL
     * 2. Opens the authorization URL in a new window for user authentication
     * 3. Handles any errors that occur during the process
     * 4. Refreshes the token validity status after the sign-in attempt
     *
     * @throws {ApiError} When the API request fails
     */
    setIsLoading(true);
    try {
      const response = await apiClient.get<{ redirect_url: string }>(
        "/nationbuilder/oauth/authorize",
      );
      const response_data = response.data;
      const redirect_url = response_data.redirect_url;

      window.open(redirect_url);
    } catch (error) {
      const apiError = error as ApiError;
      setTokenState(NationBuilderAccessTokenStatus.Invalid);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
    refreshNationBuilderTokenValidityStatus();
  };

  const refreshNationBuilderTokenValidityStatus = async () => {
    setTokenState(NationBuilderAccessTokenStatus.Loading);
    try {
      const response = await apiClient.get<{ is_valid: boolean }>(
        "/nationbuilder/oauth/is-valid",
      );
      setTokenState(
        response.data.is_valid
          ? NationBuilderAccessTokenStatus.Valid
          : NationBuilderAccessTokenStatus.Invalid,
      );
    } catch (error) {
      const apiError = error as ApiError;
      setError(apiError.message);
      setTokenState(NationBuilderAccessTokenStatus.Invalid);
    }
  };

  const handleError = async (error: ApiError) => {
    const apiError = error.data as unknown as NationBuilderTokenAPIError;
    if (apiError.type === "INVALID_NATIONBUILDER_TOKEN_ERROR") {
      setTokenState(NationBuilderAccessTokenStatus.Invalid);
      await refreshNationBuilderTokenValidityStatus();
      navigate("/nationbuilder-signin", { replace: true });
    }
  };

  // Initialize token state on mount
  useEffect(() => {
    if (isAuthenticated) {
      refreshNationBuilderTokenValidityStatus();
    }
  }, [isAuthenticated]);

  const value: NationBuilderTokenContextType = {
    tokenState,
    isLoading,
    error,
    handleNationBuilderSignIn: handleNationBuilderSignIn,
    refreshNationBuilderTokenValidityStatus:
      refreshNationBuilderTokenValidityStatus,
    handleError: handleError,
  };

  return (
    <NationBuilderTokenContext.Provider value={value}>
      {children}
    </NationBuilderTokenContext.Provider>
  );
};

export const useNationBuilderToken = (): NationBuilderTokenContextType => {
  const context = useContext(NationBuilderTokenContext);
  if (context === undefined) {
    throw new Error("useToken must be used within a TokenProvider");
  }
  return context;
};
